import { saveAs } from 'file-saver';
import { showErrorMessage, showInfoMessage } from '../dialogs';

/**
 * Download a single attachment file with authentication
 *
 * @param {number} attachmentId - The attachment ID
 * @param {string} fileName - The filename to save as
 * @param {string} token - JWT authentication token
 * @param {string} errorMessage - Error message to display on failure
 * @returns {Promise}
 */
export function downloadSingleAttachment(attachmentId, fileName, token, errorMessage) {
    const url = `${window.Config.api}/product-attachments/${attachmentId}`;

    return fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    })
    .then(response => {
        if (!response.ok) {
            throw new Error('Download failed');
        }
        return response.blob();
    })
    .then(blob => {
        saveAs(blob, fileName);
    })
    .catch(() => {
        showErrorMessage(errorMessage);
    });
}

/**
 * Download attachments as a ZIP file
 *
 * @param {Function} fetcherFunction - API function that returns attachment data
 * @param {string} name - Base name for the download
 * @param {Object} translations - Translation keys for messages
 * @param {string} translations.downloading - Optional info message shown while downloading
 * @param {string} translations.notFound - Error message for 404 responses
 * @param {string} translations.error - Generic error message
 * @returns {Promise}
 */
export function downloadAttachmentsZip(fetcherFunction, name, translations = {}) {
    if (translations.downloading) {
        showInfoMessage(translations.downloading);
    }

    return fetcherFunction()
        .then(function (response) {
            const blob = new Blob([response.data], {
                type: 'application/zip',
            });

            let filename = `${name}.zip`;

            // Extract filename from Content-Disposition header if available
            if (response.headers['content-disposition']) {
                const dispositionParts = response.headers['content-disposition'].split(';');
                if (dispositionParts.length > 1) {
                    const filenamePart = dispositionParts[1].split('=');
                    if (filenamePart.length > 1) {
                        filename = filenamePart[1];
                    }
                }
            }

            saveAs(blob, filename);
        })
        .catch((error) => {
            if (error.response?.status === 404) {
                return showErrorMessage(translations.notFound || 'No attachments found');
            }

            showErrorMessage(translations.error || 'Download failed');
        });
}
